import React, { useEffect, useState } from "react";
import axios from "axios";
import { BeatLoader } from "react-spinners";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { resolveUrl } from "../../../utils/storeUtils";
import "./StoreProductReviews.css";

function StarRow({ rating }) {
  return (
    <span className="spr-stars">
      {[1, 2, 3, 4, 5].map((s) => (
        <span key={s} className={`spr-star ${s <= rating ? "is-filled" : ""}`}>★</span>
      ))}
    </span>
  );
}

function formatDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

export default function StoreProductReviews() {
  const navigate = useNavigate();
  const token = localStorage.getItem("authToken") || sessionStorage.getItem("authToken");
  const apiBaseUrl = axios.defaults.baseURL || "";

  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  useEffect(() => {
    if (!token) { navigate("/dashboard/login"); return; }
    setLoading(true);
    axios
      .get("/store/product-reviews/admin", { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setReviews(Array.isArray(data.reviews) ? data.reviews : []))
      .catch(() => toast.error("Failed to load reviews."))
      .finally(() => setLoading(false));
  }, [token, navigate]);

  const handleToggleApproval = async (review) => {
    const next = !review.approved;
    try {
      await axios.put(
        `/store/product-reviews/admin/${review._id}`,
        { approved: next },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReviews((prev) => prev.map((r) => r._id === review._id ? { ...r, approved: next } : r));
      toast.success(next ? "Review approved." : "Review hidden.");
    } catch {
      toast.error("Failed to update review.");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`/store/product-reviews/admin/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReviews((prev) => prev.filter((r) => r._id !== id));
      toast.success("Review deleted.");
    } catch {
      toast.error("Failed to delete review.");
    } finally {
      setConfirmDeleteId(null);
    }
  };

  const getProductImage = (review) => {
    const imgs = review.productId?.images;
    const raw = Array.isArray(imgs) && imgs.length > 0 ? (imgs[0]?.url || imgs[0]) : null;
    return raw ? resolveUrl(raw, apiBaseUrl) : null;
  };

  if (loading) {
    return (
      <div className="spr-page">
        <div className="spr-loader"><BeatLoader color="#2a2520" size={10} /></div>
      </div>
    );
  }

  return (
    <div className="spr-page">
      <div className="spr-header">
        <h1 className="spr-title">Customer Reviews</h1>
        <span className="spr-count">{reviews.length} review{reviews.length !== 1 ? "s" : ""}</span>
      </div>

      {reviews.length === 0 ? (
        <p className="spr-empty">No customer reviews yet.</p>
      ) : (
        <div className="spr-list">
          {reviews.map((review) => {
            const img = getProductImage(review);
            const productName = review.productId?.nameAr || review.productId?.name || "Product";
            return (
              <div key={review._id} className={`spr-card ${review.approved ? "" : "spr-card--hidden"}`}>
                <div className="spr-card-product">
                  {img && <img src={img} alt="" className="spr-product-img" />}
                  <span className="spr-product-name">{productName}</span>
                </div>
                <div className="spr-card-body">
                  <div className="spr-card-top">
                    <span className="spr-customer-name">{review.customerName}</span>
                    <StarRow rating={review.rating} />
                    <span className="spr-date">{formatDate(review.createdAt)}</span>
                    <span className={`spr-status-badge ${review.approved ? "spr-status-badge--approved" : "spr-status-badge--hidden"}`}>
                      {review.approved ? "Visible" : "Hidden"}
                    </span>
                  </div>
                  {review.comment && <p className="spr-comment">"{review.comment}"</p>}
                  <div className="spr-actions">
                    <button
                      type="button"
                      className={`spr-btn ${review.approved ? "spr-btn--hide" : "spr-btn--approve"}`}
                      onClick={() => handleToggleApproval(review)}
                    >
                      {review.approved ? "Hide" : "Approve"}
                    </button>
                    <button
                      type="button"
                      className="spr-btn spr-btn--delete"
                      onClick={() => setConfirmDeleteId(review._id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {confirmDeleteId && (
        <div className="spr-modal-overlay">
          <div className="spr-modal">
            <p className="spr-modal-text">Delete this review? This cannot be undone.</p>
            <div className="spr-modal-actions">
              <button type="button" className="spr-btn spr-btn--cancel" onClick={() => setConfirmDeleteId(null)}>Cancel</button>
              <button type="button" className="spr-btn spr-btn--delete" onClick={() => handleDelete(confirmDeleteId)}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
